import { create } from 'zustand';

export type SelectionMode = 'bbox' | 'polygon' | 'none';

export interface MapState {
  center: [number, number];
  zoom: number;
  bbox: [number, number, number, number] | null;
  polygonVertices: [number, number][];
  selectionMode: SelectionMode;
  mapStyle: string;

  setCenter: (c: [number, number]) => void;
  setZoom: (z: number) => void;
  setBbox: (b: [number, number, number, number] | null) => void;
  setPolygonVertices: (v: [number, number][]) => void;
  setSelectionMode: (m: SelectionMode) => void;
  resetBbox: () => void;
  flyTo: (center: [number, number], zoom?: number) => void;

  // Computed
  getBboxAreaKm2: () => number | null;
  isBboxValid: () => boolean;
}

function haversineDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371; // Earth radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export const useMapStore = create<MapState>((set, get) => ({
  center: [151.2093, -33.8688], // Sydney CBD default
  zoom: 14,
  bbox: null,
  polygonVertices: [],
  selectionMode: 'bbox',
  mapStyle: 'carto-dark',

  setCenter: (c) => set({ center: c }),
  setZoom: (z) => set({ zoom: z }),
  setBbox: (b) => set({ bbox: b }),
  setPolygonVertices: (v) => set({ polygonVertices: v }),
  setSelectionMode: (m) => set({ selectionMode: m }),
  resetBbox: () => set({ bbox: null, polygonVertices: [] }),
  flyTo: (center, zoom) =>
    set((state) => ({
      center,
      zoom: zoom ?? state.zoom,
    })),

  getBboxAreaKm2: () => {
    const bbox = get().bbox;
    if (!bbox) return null;
    const [minLng, minLat, maxLng, maxLat] = bbox;
    const width = haversineDistance(minLat, minLng, minLat, maxLng);
    const height = haversineDistance(minLat, minLng, maxLat, minLng);
    return width * height;
  },

  isBboxValid: () => {
    const bbox = get().bbox;
    if (!bbox) return false;
    const [minLng, minLat, maxLng, maxLat] = bbox;
    const area = get().getBboxAreaKm2();
    return (
      minLng < maxLng &&
      minLat < maxLat &&
      area !== null &&
      area > 0 &&
      area < 10000 // Max 10,000 km²
    );
  },
}));
